import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { BiRefresh } from "react-icons/bi";
import { GiRobotGolem } from "react-icons/gi";
import {
  Button,
  Input,
  Textarea,
  Card,
  CardBody,
  Typography,
} from "@material-tailwind/react";
import { toast } from "react-toastify";
import { motion, useAnimate } from "framer-motion";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { PROXY } from "../lib/utils";

const createIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const value = Math.floor(Math.random() * 16);
    return (character === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
};

const ContactUs = () => {
  const { t, i18n } = useTranslation();
  const [challengeAnswer, setChallengeAnswer] = useState("");
  const [challengeError, setChallengeError] = useState("");
  const [isSubmittingExtra, setIsSubmittingExtra] = useState(false);
  const submissionInProgress = useRef(false);
  const idempotencyKey = useRef(null);

  const challengeOptions = useMemo(
    () => [
      {
        id: "sky",
        question: t("contact.challenge.sky"),
        answers: ["blue", "azul", "آبي", "آبی", "شین", "شين"],
      },
      {
        id: "capital",
        question: t("contact.challenge.capital"),
        answers: ["kabul", "kaboul", "کابل", "کابول"],
      },
      {
        id: "banana",
        question: t("contact.challenge.banana"),
        answers: ["yellow", "zard", "زرد", "زیړ", "ژېړ"],
      },
      {
        id: "human",
        question: t("contact.challenge.human"),
        answers: ["4", "four", "۴", "٤"],
      },
      {
        id: "math2",
        question: t("contact.challenge.math2"),
        answers: ["7", "seven", "هفت", "اوه"],
      },
    ],
    [t],
  );
  const [activeChallenge, setActiveChallenge] = useState(challengeOptions[0]);

  useEffect(() => {
    setActiveChallenge(challengeOptions[0]);
  }, [challengeOptions]);

  const hintKey = activeChallenge
    ? `contact.challenge.hints.${activeChallenge.id}`
    : "";
  const hintAvailable = Boolean(hintKey && i18n.exists(hintKey));
  const hintText = hintAvailable ? t(hintKey) : "";

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t("validation.name_required"))
      .min(3, t("validation.name_min"))
      .max(50, t("validation.name_max")),

    email: Yup.string()
      .required(t("validation.email_required"))
      .email(t("validation.email_invalid")),

    phone: Yup.string()
      .required(t("validation.phone_required"))
      .matches(/^[\d()+\s-]+$/, t("validation.phone_invalid"))
      .test("phone-format", t("validation.phone_invalid"), (value) => {
        if (!value) return false;
        const digits = value.replace(/[^\d]/g, "");
        if (!digits) return false;

        if (digits.startsWith("07")) {
          return digits.length === 10;
        }

        if (digits.startsWith("93")) {
          return digits.length === 11;
        }

        if (digits.startsWith("0093")) {
          return digits.length === 13;
        }

        return digits.length >= 9 && digits.length <= 13;
      }),

    subject: Yup.string()
      .required(t("validation.subject_required"))
      .min(5, t("validation.subject_min"))
      .max(100, t("validation.subject_max")),

    message: Yup.string()
      .required(t("validation.message_required"))
      .min(10, t("validation.message_min"))
      .max(1000, t("validation.message_max")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  // Handle form submission
  const onSubmit = async (data) => {
    if (submissionInProgress.current) return;
    if (!activeChallenge) {
      toast.error(t("contact.error.challenge"));
      return;
    }

    if (!challengeAnswer.trim()) {
      setChallengeError(t("contact.error.challenge_empty"));
      toast.error(t("contact.error.challenge_empty"));
      return;
    }

    const normalized = challengeAnswer.trim().toLowerCase();
    const isValid = activeChallenge.answers.includes(normalized);
    if (!isValid) {
      setChallengeError(t("contact.error.challenge_invalid"));
      toast.error(t("contact.error.challenge_invalid"));
      return;
    }

    setChallengeError("");
    submissionInProgress.current = true;
    setIsSubmittingExtra(true);
    idempotencyKey.current = idempotencyKey.current || createIdempotencyKey();

    try {
      await axios.post(`${PROXY}/contact-messages`, {
        ...data,
        challengeId: activeChallenge.id,
        challengeResponse: normalized,
      }, {
        headers: { "Idempotency-Key": idempotencyKey.current },
      });

      toast.success(t("contact.success"));
      reset();
      idempotencyKey.current = null;
      setChallengeAnswer("");
      setActiveChallenge((prev) => {
        const nextList = challengeOptions.filter((opt) => opt.id !== prev?.id);
        return nextList[Math.floor(Math.random() * nextList.length)] || prev;
      });
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || t("toast.generic"));
    } finally {
      submissionInProgress.current = false;
      setIsSubmittingExtra(false);
    }
  };

  // Animation control
  const [scope, animate] = useAnimate();

  useEffect(() => {
    animate(scope.current, { opacity: [0, 1], y: [50, 0] }, { duration: 0.5 });
  }, [animate, scope]);

  const rotateChallenge = () => {
    setActiveChallenge((prev) => {
      const candidates = challengeOptions.filter((opt) => opt.id !== prev?.id);
      const next = candidates[Math.floor(Math.random() * candidates.length)];
      return next || prev;
    });
    setChallengeAnswer("");
    setChallengeError("");
  };

  return (
    <section
      ref={scope}
      className="container mx-auto px-4 sm:px-6 lg:px-8 mb-20 mt-5 border-t-gray-500/10"
    >
      <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 text-center pt-10">
        <div className="relative p-4">
          <FaPhoneAlt className="text-3xl text-indigo-500 mx-auto mb-2" />
          <Typography variant="h6" className="mb-2">
            {t("our-telephone")}
          </Typography>
          <Typography className="text-gray-700">
            {t("telephone-number")}
          </Typography>
          {/* <Typography className="text-gray-700">+0202502278</Typography> */}
          <div className="absolute bottom-0 right-0 w-10 h-10 bg-indigo-500 opacity-20 rounded-full transform translate-x-5 translate-y-5"></div>
        </div>
        <div className="relative p-4">
          <FaEnvelope className="text-3xl text-indigo-500 mx-auto mb-2" />
          <Typography variant="h6" className="mb-2">
            {t("send-us-mail")}
          </Typography>
          <Typography className="text-gray-700">
            elearning_dept@mohe.gov.af
          </Typography>
          {/* <Typography className="text-gray-700">help@ourmail.com</Typography> */}
          <div className="absolute bottom-0 right-0 w-10 h-10 bg-indigo-500 opacity-20 rounded-full transform translate-x-5 translate-y-5"></div>
        </div>
        <div className="relative p-4">
          <FaMapMarkerAlt className="text-3xl text-indigo-500 mx-auto mb-2" />
          <Typography variant="h6" className="mb-2">
            {t("address")}
          </Typography>
          <Typography className="text-gray-700"></Typography>
          <Typography className="text-gray-700">
            {t("address-description")}
          </Typography>
        </div>
      </motion.div>

      <motion.div className="max-w-3xl w-full mx-auto mt-12 sm:mt-16">
        <Card className="shadow-lg">
          <CardBody className="p-6 sm:p-8">
            <Typography variant="h5" className="text-center mb-6">
              {t("have-a-question")}
            </Typography>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div>
                <Input
                  type="text"
                  label={t("form.your-full-name")}
                  {...register("name")}
                  error={Boolean(errors.name)}
                  disabled={isSubmitting}
                  color="indigo"
                  className="mb-4"
                  containerProps={{ className: "w-full" }}
                />
                {errors.name && (
                  <Typography color="red" className="text-sm">
                    {errors.name.message}
                  </Typography>
                )}
              </div>
              <div>
                <Input
                  type="email"
                  label={t("form.your-email")}
                  {...register("email")}
                  error={Boolean(errors.email)}
                  disabled={isSubmitting}
                  color="indigo"
                  className="mb-4"
                  containerProps={{ className: "w-full" }}
                />
                {errors.email && (
                  <Typography color="red" className="text-sm">
                    {errors.email.message}
                  </Typography>
                )}
              </div>
              <div>
                <Input
                  type="text"
                  label={t("form.phone-number")}
                  {...register("phone")}
                  error={Boolean(errors.phone)}
                  disabled={isSubmitting}
                  color="indigo"
                  className="mb-4"
                  containerProps={{ className: "w-full" }}
                />
                {errors.phone && (
                  <Typography color="red" className="text-sm">
                    {errors.phone.message}
                  </Typography>
                )}
              </div>
              <div>
                <Input
                  type="text"
                  label={t("form.subject")}
                  {...register("subject")}
                  error={Boolean(errors.subject)}
                  disabled={isSubmitting}
                  color="indigo"
                  className="mb-4"
                  containerProps={{ className: "w-full" }}
                />
                {errors.subject && (
                  <Typography color="red" className="text-sm">
                    {errors.subject.message}
                  </Typography>
                )}
              </div>
              <div className="col-span-1 md:col-span-2">
                <Textarea
                  label={t("form.write-a-message")}
                  {...register("message")}
                  error={Boolean(errors.message)}
                  disabled={isSubmitting}
                  color="indigo"
                  className="mb-4"
                  containerProps={{ className: "w-full" }}
                />
                {errors.message && (
                  <Typography color="red" className="text-sm">
                    {errors.message.message}
                  </Typography>
                )}
              </div>
              <div className="col-span-1 md:col-span-2">
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/90 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Typography className="text-sm font-semibold text-indigo-800">
                        {t("contact.challenge.label")}
                      </Typography>
                      <p className="text-xs text-indigo-600">
                        {t("contact.challenge.description")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={rotateChallenge}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium inline-flex items-center gap-1"
                    >
                      <BiRefresh className="text-lg" />
                      {t("contact.challenge.refresh")}
                    </button>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700">
                      {activeChallenge?.question}
                    </p>
                    {hintAvailable && (
                      <Typography className="text-xs text-indigo-500">
                        {hintText}
                      </Typography>
                    )}
                    <Input
                      type="text"
                      value={challengeAnswer}
                      onChange={(event) =>
                        setChallengeAnswer(event.target.value)
                      }
                      label={t("contact.challenge.input-label")}
                      disabled={isSubmitting || isSubmittingExtra}
                      color="indigo"
                      containerProps={{ className: "w-full" }}
                    />
                    {challengeError && (
                      <Typography color="red" className="text-xs">
                        {challengeError}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-1 md:col-span-2 text-center">
                <Button
                  type="submit"
                  color="indigo"
                  disabled={isSubmitting || isSubmittingExtra}
                  className="w-full md:w-auto"
                >
                  {isSubmitting || isSubmittingExtra
                    ? t("form.sending")
                    : t("form.btn-send-message")}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </motion.div>
      {/* <motion.div className="max-w-3xl w-full mx-auto mt-8">
        <Card className="shadow-lg border border-dashed border-indigo-200 bg-gradient-to-b from-indigo-50 to-white">
          <CardBody className="p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-4">
            <div className="h-20 w-20 rounded-full bg-white shadow flex items-center justify-center">
              <GiRobotGolem className="h-10 w-10 text-indigo-500" />
            </div>
            <div>
              <Typography variant="h6" className="text-indigo-900">
                {t("contact.robot.title")}
              </Typography>
              <p className="text-sm text-gray-600">
                {t("contact.robot.description")}
              </p>
            </div>
          </CardBody>
        </Card>
      </motion.div> */}
    </section>
  );
};

export default ContactUs;
